import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { databases, DATABASE_ID, EVENTS_COLLECTION_ID, Query, ID } from '../config/appwrite';

interface AdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventCreated?: () => void;
}

const AdminModal: React.FC<AdminModalProps> = ({ isOpen, onClose, onEventCreated }) => {
  const [newEvent, setNewEvent] = useState({ name: '', youtube_link: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateEvent = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);

      const youtubeIdRegex = /^[a-zA-Z0-9_-]+$/;
      if (!youtubeIdRegex.test(newEvent.youtube_link)) {
        toast.error('Please enter a valid YouTube video ID (letters, numbers, _ and - only)');
        setIsLoading(false);
        return;
      }

      try {
        const response = await databases.listDocuments(DATABASE_ID, EVENTS_COLLECTION_ID, [
          Query.equal('is_active', true),
        ]);
        for (const doc of response.documents) {
          await databases.updateDocument(DATABASE_ID, EVENTS_COLLECTION_ID, doc.$id, {
            is_active: false,
          });
        }

        await databases.createDocument(DATABASE_ID, EVENTS_COLLECTION_ID, ID.unique(), {
          name: newEvent.name,
          youtube_link: newEvent.youtube_link,
          is_active: true,
          created_at: new Date().toISOString(),
        });

        setNewEvent({ name: '', youtube_link: '' });
        onEventCreated?.();
        onClose();
        toast.success('Event created successfully!');
      } catch (error) {
        console.error('Error creating event:', error);
        toast.error('Error creating event. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [newEvent, onEventCreated, onClose]
  );

  const handleYoutubeIdChange = (value: string) => {
    const idMatch = value.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (idMatch) {
      setNewEvent({ ...newEvent, youtube_link: idMatch[1] });
    } else {
      setNewEvent({ ...newEvent, youtube_link: value.replace(/[^a-zA-Z0-9_-]/g, '') });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-[#0e4653] px-6 py-5 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Create New Event</h3>
            <p className="text-[#7fb8c8] text-xs mt-0.5">New event will automatically become active</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleCreateEvent} className="p-6 space-y-5">

          {/* Event Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              Event Name
            </label>
            <input
              type="text"
              value={newEvent.name}
              onChange={e => setNewEvent({ ...newEvent, name: e.target.value })}
              required
              disabled={isLoading}
              placeholder="Enter event name"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/25 focus:border-[#0e4653] disabled:bg-gray-50 transition"
            />
          </div>

          {/* YouTube ID */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              YouTube Video ID
            </label>

            {/* Info card */}
            <div className="flex gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-amber-800 space-y-0.5">
                <p>Enter only the video ID, not the full URL.</p>
                <p>
                  From <span className="font-mono">youtube.com/watch?v=<span className="bg-amber-100 px-1 rounded font-semibold">dQw4w9WgXcQ</span></span>
                </p>
                <p>
                  Enter: <span className="font-mono bg-amber-100 px-1 rounded font-semibold">dQw4w9WgXcQ</span>
                </p>
              </div>
            </div>

            <input
              type="text"
              value={newEvent.youtube_link}
              onChange={e => handleYoutubeIdChange(e.target.value)}
              required
              disabled={isLoading}
              placeholder="dQw4w9WgXcQ"
              pattern="[a-zA-Z0-9_-]+"
              title="Enter a valid YouTube video ID"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0e4653]/25 focus:border-[#0e4653] disabled:bg-gray-50 transition"
            />
            {newEvent.youtube_link && (
              <p className="text-xs text-gray-400 mt-1.5">
                Preview ID: <span className="font-mono text-[#0e4653] font-medium">{newEvent.youtube_link}</span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-[#B98027] text-white rounded-lg text-sm font-semibold hover:bg-[#a06f20] focus:outline-none focus:ring-2 focus:ring-[#B98027]/40 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Creating…
                </span>
              ) : (
                'Create Event'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminModal;
